import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
  type IAuthenticationCallback,
} from 'amazon-cognito-identity-js';
import { jwtDecode } from 'jwt-decode';
import { useEffect, useState } from 'react';
import type { AuthProvider } from 'react-admin';
import type { Permissions } from '../types';
import { EMPTY_PERMISSIONS } from '../types';
import { isUnprovisionedError } from './auth-helpers';

const API_BASE = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/$/, '');

interface MePermissionsResponse {
  provisioned?: boolean;
  assignments?: Permissions['assignments'];
  capabilities?: Permissions['capabilities'];
}

export const SESSION_CHANGED_CODE = 'SESSION_CHANGED';
const SESSION_EXPIRED_CODE = 'SESSION_EXPIRED';

export const sessionChangedError = (message: string): Error =>
  Object.assign(new Error(message), { code: SESSION_CHANGED_CODE });

export const isSessionChangedError = (error: unknown): boolean =>
  typeof error === 'object' && error !== null &&
  (error as { code?: unknown }).code === SESSION_CHANGED_CODE;

export const isSessionExpiredError = (error: unknown): boolean =>
  typeof error === 'object' && error !== null &&
  (error as { code?: unknown }).code === SESSION_EXPIRED_CODE;

async function fetchMe(signal?: AbortSignal): Promise<MePermissionsResponse> {
  if (!API_BASE || !currentIdToken) throw Object.assign(new Error('An authenticated API session is required'), { status: 401 });
  const revision = sessionRevision;
  const generation = sessionGeneration;
  const token = currentIdToken;
  const assertCurrent = () => {
    if (
      revision !== sessionRevision ||
      generation !== sessionGeneration ||
      token !== currentIdToken
    ) {
      throw sessionChangedError('Session changed while loading permissions');
    }
  };
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/me`, {
      cache: 'no-store', credentials: 'omit', referrerPolicy: 'no-referrer',
      signal,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
  } catch (error) {
    assertCurrent();
    throw error;
  }
  assertCurrent();
  if (!res.ok) {
    const err = new Error(`/me returned ${res.status}`);
    (err as any).status = res.status;
    throw err;
  }
  let data: MePermissionsResponse;
  try {
    data = await res.json() as MePermissionsResponse;
  } catch (error) {
    assertCurrent();
    throw error;
  }
  assertCurrent();
  return data;
}

const UserPoolId = import.meta.env.VITE_USER_POOL_ID;
const ClientId = import.meta.env.VITE_CLIENT_ID;


const userPool = new CognitoUserPool({ UserPoolId, ClientId });

// Compatibility handle for retained, unreachable direct-AWS adapters; never issued.
let awsCredentials: {
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken: string;
} | null = null;

// Current authenticated API session token.
let currentIdToken: string | null = null;
let acceptedCognitoSession: {
  user: CognitoUser;
  session: CognitoUserSession;
  token: string;
} | null = null;

export const getAwsCredentials = () => awsCredentials;
let sessionRevision = 0;
let sessionGeneration = 0;
const sessionListeners = new Set<() => void>();
let _on403Callback: (() => void) | null = null;
let _403Timer: ReturnType<typeof setTimeout> | null = null;
const _dispatched403Operations = new WeakMap<object, {
  revision: number;
  generation: number;
}>();
export const getSessionRevision = (): number => sessionRevision;
export const getSessionGeneration = (): number => sessionGeneration;
export const subscribeSession = (listener: () => void): (() => void) => {
  sessionListeners.add(listener);
  return () => { sessionListeners.delete(listener); };
};
const notifySession = () => {
  if (_403Timer !== null) {
    clearTimeout(_403Timer);
    _403Timer = null;
  }
  sessionGeneration++;
  for (const listener of sessionListeners) listener();
};
const changeSession = () => {
  sessionRevision++;
  notifySession();
};
const invalidateSession = () => {
  const changed = currentIdToken !== null || awsCredentials !== null;
  currentIdToken = null;
  acceptedCognitoSession = null;
  awsCredentials = null;
  clearRefreshTimer();
  if (changed) changeSession();
};
export const getIdToken = (): string | null => currentIdToken;

// ----- 403 notification (debounced and bound to the originating session) -----

export const set403Callback = (fn: () => void): void => {
  _on403Callback = fn;
};

export const dispatch403 = (
  expectedRevision = sessionRevision,
  expectedGeneration = sessionGeneration,
  operation: object = {},
): void => {
  const prior = _dispatched403Operations.get(operation);
  if (
    !_on403Callback ||
    expectedRevision !== sessionRevision ||
    expectedGeneration !== sessionGeneration ||
    (prior?.revision === expectedRevision && prior.generation === expectedGeneration)
  ) return;
  _dispatched403Operations.set(operation, {
    revision: expectedRevision,
    generation: expectedGeneration,
  });
  // Coalesce independent denials in the same short page burst, while the
  // operation key above suppresses retries of one query after this timer ends.
  if (_403Timer !== null) return;
  _403Timer = setTimeout(() => {
    _403Timer = null;
    if (
      expectedRevision === sessionRevision &&
      expectedGeneration === sessionGeneration
    ) {
      _on403Callback?.();
    }
  }, 100);
};

// ----- Unprovisioned-account flag -----

let _unprovisioned = false;
let _onUnprovisionedChange: ((v: boolean) => void) | null = null;

export const setUnprovisionedCallback = (fn: (v: boolean) => void): void => {
  _onUnprovisionedChange = fn;
};

export const getUnprovisionedFlag = (): boolean => _unprovisioned;

const setUnprovisioned = (v: boolean): void => {
  _unprovisioned = v;
  _onUnprovisionedChange?.(v);
};

// ----- Proactive session refresh -----

let refreshTimerId: ReturnType<typeof setTimeout> | null = null;
let onSessionExpiredCallback: (() => void) | null = null;

export const setSessionExpiredCallback = (fn: () => void): void => {
  onSessionExpiredCallback = fn;
};

export const dispatchSessionExpired = (): void => {
  onSessionExpiredCallback?.();
};

/** Expire only the exact session that received the terminal authentication failure. */
export const expireSessionIfCurrent = (
  revision: number,
  generation: number,
  token: string,
): boolean => {
  if (
    revision !== sessionRevision ||
    generation !== sessionGeneration ||
    token !== currentIdToken
  ) return false;
  invalidateSession();
  dispatchSessionExpired();
  return true;
};

export const clearRefreshTimer = (): void => {
  if (refreshTimerId !== null) {
    clearTimeout(refreshTimerId);
    refreshTimerId = null;
  }
};

// Schedules checkCredentials ~5 min before the token expires; called by
// setCredentials on every successful login or refresh so it self-reschedules.
// The Cognito SDK writes the refreshed idToken back to localStorage on each
// getSession call, so the auth-shim (which reads idToken from localStorage)
// always sees an up-to-date value without any extra work here.
const scheduleRefresh = (token: string): void => {
  clearRefreshTimer();
  const revision = sessionRevision;
  const generation = sessionGeneration;
  try {
    const { exp } = jwtDecode<{ exp: number }>(token);
    const delay = Math.max(exp * 1000 - Date.now() - 5 * 60 * 1000, 0);
    refreshTimerId = setTimeout(() => {
      checkCredentials()
        .then((newToken) => {
          if (typeof newToken === 'string') scheduleRefresh(newToken);
        })
        .catch(() => {
          // checkCredentials expires an invalid current session itself. This
          // fallback covers an unexpected failure only while the exact session
          // that scheduled this timer is still current.
          expireSessionIfCurrent(revision, generation, token);
        });
    }, delay);
  } catch {
    // Malformed or missing token — skip scheduling
  }
};

/** Direct browser AWS credentials are disabled. All portal data uses the API. */
export const ensureCredentials = async (): Promise<void> => {
  throw new Error('Direct browser AWS access is disabled; use the authorized API');
};

const setCredentials = (session: CognitoUserSession, user: CognitoUser): string => {
  const idToken = session.getIdToken().getJwtToken();
  const tokenChanged = currentIdToken !== idToken;
  let principalChanged = !currentIdToken;
  try {
    const identity = (token: string) => {
      const claims = jwtDecode<Record<string, unknown>>(token);
      return JSON.stringify([claims.iss, claims.sub, claims.tenantId, claims['custom:tenantId'], claims['cognito:groups']]);
    };
    principalChanged ||= identity(idToken) !== identity(currentIdToken!);
  }
  catch { principalChanged = true; }
  currentIdToken = idToken;
  acceptedCognitoSession = { user, session, token: idToken };
  if (principalChanged) changeSession();
  else if (tokenChanged) notifySession();
  awsCredentials = null; // Direct browser credential issuance remains disabled.
  scheduleRefresh(idToken);
  return idToken;
};

export const checkCredentials = (): Promise<string | void> => {
  if (pendingMfa || pendingNewPasswordUser) {
    return Promise.reject(new Error('Sign-in challenge must be completed'));
  }
  const revision = sessionRevision;
  const generation = sessionGeneration;
  const token = currentIdToken;
  const cognitoUser = userPool.getCurrentUser();
  if (!cognitoUser) {
    const expired = token ? expireSessionIfCurrent(revision, generation, token) : false;
    if (!expired) invalidateSession();
    return Promise.reject(Object.assign(new Error('No active session'), {
      ...(expired ? { code: SESSION_EXPIRED_CODE } : {}),
    }));
  }
  const version = authenticationVersion;
  return new Promise((resolve, reject) => {
    cognitoUser.getSession(
      (err: Error | null, session: CognitoUserSession | null) => {
        if (
          revision !== sessionRevision ||
          generation !== sessionGeneration ||
          version !== authenticationVersion ||
          token !== currentIdToken
        ) {
          return reject(sessionChangedError('Session changed during refresh'));
        }
        if (err || !session?.isValid()) {
          const expired = token ? expireSessionIfCurrent(revision, generation, token) : false;
          if (!expired) invalidateSession();
          const failure = err || new Error('Invalid session');
          if (expired) Object.assign(failure, { code: SESSION_EXPIRED_CODE });
          return reject(failure);
        }
        const refreshedToken = setCredentials(session, cognitoUser);
        resolve(refreshedToken);
      },
    );
  });
};

export const useAuthLoading = (): boolean => {
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    checkCredentials()
      .then(() => setLoading(false))
      .catch(() => setLoading(false));
  }, []);
  return loading;
};

export const getDecodedToken = (
  cognitoUser = userPool.getCurrentUser(),
): Record<string, string> | null => {
  if (!cognitoUser) return null;
  try {
    const username = cognitoUser.getUsername();
    const token = localStorage.getItem(`CognitoIdentityServiceProvider.${ClientId}.${username}.idToken`);
    if (token) return jwtDecode(token);
  } catch (e) {
    console.error(e);
  }
  return null;
};

export const getUserId = (): string | null => {
  return localStorage.getItem(`CognitoIdentityServiceProvider.${ClientId}.LastAuthUser`);
};

export const forgotPassword = (username: string): Promise<void> => {
  const cognitoUser = new CognitoUser({ Username: username, Pool: userPool });
  return new Promise((resolve, reject) => {
    cognitoUser.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
      inputVerificationCode: () => resolve(),
    });
  });
};

export const confirmForgotPassword = (
  username: string,
  code: string,
  newPassword: string,
): Promise<void> => {
  const cognitoUser = new CognitoUser({ Username: username, Pool: userPool });
  return new Promise((resolve, reject) => {
    cognitoUser.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
};

// Stash for the NEW_PASSWORD_REQUIRED first-login challenge.
// pendingRequiredAttributes are the names Cognito says MUST be freshly set
// (from the callback's second arg). The current-state userAttributes from
// the callback's first arg are intentionally NOT stashed — echoing them back
// causes Cognito to treat each key as an update, which fails for immutable
// attributes (see AP-001 in docs/DEFECTS.md).
let pendingNewPasswordUser: InstanceType<typeof CognitoUser> | null = null;
let pendingRequiredAttributes: string[] = [];
let authenticationVersion = 0;
type MfaKind = 'totp' | 'setup' | 'selection';
interface PendingMfa {
  user: CognitoUser;
  kind: MfaKind;
  version: number;
  issuedAt: number;
  monotonicAt: number;
  associated: boolean;
}
let pendingMfa: PendingMfa | null = null;
const MFA_WINDOW_MS = 3 * 60 * 1000;

const challengeError = () => Object.assign(
  new Error('This sign-in challenge expired or changed. Start sign-in again.'),
  { code: 'CHALLENGE_EXPIRED' },
);

const clearPendingChallengeState = (): void => {
  authenticationVersion++;
  pendingMfa = null;
  pendingNewPasswordUser = null;
  pendingRequiredAttributes = [];
};

const activeMfa = (kind?: MfaKind): PendingMfa => {
  const pending = pendingMfa;
  if (
    !pending ||
    pending.version !== authenticationVersion ||
    (kind && pending.kind !== kind) ||
    Date.now() < pending.issuedAt - 5000 ||
    Date.now() - pending.issuedAt >= MFA_WINDOW_MS ||
    performance.now() - pending.monotonicAt >= MFA_WINDOW_MS
  ) {
    // Only the currently installed pending challenge can be cleared here.
    // A late callback from an older generation never reaches activeMfa after
    // the version check in authenticationCallbacks.
    if (pending) clearPendingChallengeState();
    throw challengeError();
  }
  return pending;
};

export const clearNewPasswordChallenge = (): void => {
  clearPendingChallengeState();
};

// Cognito requires a non-null value for every name in requiredAttributes.
// The UI does not (yet) prompt for these, so we synthesize a benign default
// per attribute. The user has already proven identity via the SRP handshake,
// so silently supplying a placeholder is safe — the user can overwrite it
// from account settings later.
const defaultForRequiredAttribute = (name: string): string => {
  switch (name) {
    case 'name':
    case 'given_name':
    case 'family_name':
    case 'nickname':
    case 'preferred_username':
      return 'user';
    default:
      return '';
  }
};

/** Every callback belongs to one sign-in attempt; stale SDK completions cannot install a session. */
const authenticationCallbacks = (
  user: CognitoUser,
  version: number,
  resolve: (value?: void) => void,
  reject: (reason?: unknown) => void,
): IAuthenticationCallback => {
  const cleanStaleSdkSession = () => {
    const accepted = acceptedCognitoSession;
    try {
      user.signOut?.();
    } catch {
      // Restoration below is authoritative when a current session exists.
    }

    if (!accepted || currentIdToken !== accepted.token) return;
    try {
      accepted.user.setSignInUserSession(accepted.session);
      const username = accepted.user.getUsername();
      const keyPrefix = `CognitoIdentityServiceProvider.${ClientId}`;
      const restoredUser = userPool.getCurrentUser();
      if (
        acceptedCognitoSession !== accepted ||
        currentIdToken !== accepted.token ||
        restoredUser?.getUsername() !== username ||
        localStorage.getItem(`${keyPrefix}.LastAuthUser`) !== username ||
        localStorage.getItem(`${keyPrefix}.${username}.idToken`) !== accepted.token
      ) {
        throw new Error('The accepted Cognito session cache could not be restored');
      }
    } catch {
      // Never retain an in-memory token when the exact accepted SDK session
      // cannot be restored for subsequent getCurrentUser/getSession calls.
      if (acceptedCognitoSession === accepted && currentIdToken === accepted.token) {
        try { accepted.user.signOut?.(); } catch { /* invalidate below */ }
        invalidateSession();
      }
    }
  };

  const rejectUnsupported = () => {
    if (version !== authenticationVersion) {
      reject(challengeError());
      return;
    }
    clearPendingChallengeState();
    reject(new Error('This account requires an authentication method not supported by this portal.'));
  };

  const stage = (kind: MfaKind, code: string) => {
    if (version !== authenticationVersion) {
      reject(challengeError());
      return;
    }
    const prior = pendingMfa;
    if (prior) {
      try {
        activeMfa();
      } catch (error) {
        reject(error);
        return;
      }
    }
    pendingMfa = {
      user,
      kind,
      version,
      issuedAt: prior?.issuedAt ?? Date.now(),
      monotonicAt: prior?.monotonicAt ?? performance.now(),
      associated: false,
    };
    reject({ code });
  };

  return {
    onSuccess: session => {
      try {
        if (version !== authenticationVersion) throw challengeError();
        if (pendingMfa) activeMfa();
      } catch (error) {
        // The SDK caches before invoking this callback. Remove a stale cached
        // session rather than allowing it to be restored on the next refresh.
        // Do not invalidate the module-wide session: a newer authentication
        // generation may already have installed a different principal.
        cleanStaleSdkSession();
        reject(error);
        return;
      }
      clearNewPasswordChallenge();
      setCredentials(session, user);
      resolve();
    },
    onFailure: error => {
      if (version !== authenticationVersion) {
        reject(challengeError());
        return;
      }
      const code = error?.code ?? error?.name;
      if (code === 'NotAuthorizedException' || code === 'ExpiredCodeException') {
        clearNewPasswordChallenge();
      }
      reject(error);
    },
    newPasswordRequired: (_attributes, requiredAttributes = []) => {
      if (version !== authenticationVersion) {
        reject(challengeError());
        return;
      }
      pendingNewPasswordUser = user;
      pendingRequiredAttributes = requiredAttributes;
      reject({ code: 'NEW_PASSWORD_REQUIRED' });
    },
    totpRequired: () => stage('totp', 'SOFTWARE_TOKEN_MFA'),
    mfaSetup: () => stage('setup', 'MFA_SETUP'),
    selectMFAType: (_name, parameters) => {
      let choices: string[] = [];
      try {
        const parsed: unknown = JSON.parse(parameters?.MFAS_CAN_CHOOSE ?? '[]');
        if (Array.isArray(parsed)) {
          choices = parsed.filter((choice): choice is string => typeof choice === 'string');
        }
      } catch {
        // Fail closed below when Cognito returns malformed challenge parameters.
      }
      if (!choices.includes('SOFTWARE_TOKEN_MFA')) {
        rejectUnsupported();
        return;
      }
      stage('selection', 'SELECT_SOFTWARE_MFA');
    },
    mfaRequired: rejectUnsupported,
    customChallenge: rejectUnsupported,
  };
};

export const completeNewPassword = (newPassword: string): Promise<void> => {
  if (!pendingNewPasswordUser) {
    return Promise.reject(new Error('No pending NEW_PASSWORD_REQUIRED challenge'));
  }
  const user = pendingNewPasswordUser;
  const requiredAttrs = Object.fromEntries(
    pendingRequiredAttributes.map((name) => [name, defaultForRequiredAttribute(name)]),
  );
  return new Promise((resolve, reject) => {
    user.completeNewPasswordChallenge(
      newPassword,
      requiredAttrs,
      authenticationCallbacks(user, authenticationVersion, resolve, reject),
    );
  });
};

export const selectSoftwareMfa = async (): Promise<void> => {
  const pending = activeMfa('selection');
  return new Promise((resolve, reject) => {
    pending.user.sendMFASelectionAnswer(
      'SOFTWARE_TOKEN_MFA',
      authenticationCallbacks(pending.user, pending.version, resolve, reject),
    );
  });
};

/** The setup secret stays in component memory; never persist or log it. */
export const beginSoftwareTokenSetup = async (): Promise<string> => {
  const pending = activeMfa('setup');
  return new Promise((resolve, reject) => {
    pending.user.associateSoftwareToken({
      associateSecretCode: secret => {
        try {
          if (pending.version !== authenticationVersion || pendingMfa !== pending) {
            throw challengeError();
          }
          if (activeMfa('setup') !== pending || !secret) throw challengeError();
          pending.associated = true;
          resolve(secret);
        } catch (error) {
          reject(error);
        }
      },
      onFailure: error => {
        if (pending.version !== authenticationVersion || pendingMfa !== pending) {
          reject(challengeError());
          return;
        }
        const code = error?.code ?? error?.name;
        if (code === 'NotAuthorizedException' || code === 'ExpiredCodeException') {
          clearPendingChallengeState();
        }
        reject(error);
      },
    });
  });
};

export const completeSoftwareTokenMfa = async (code: string): Promise<void> => {
  const pending = activeMfa();
  if (!/^\d{6}$/.test(code)) {
    throw new Error('Enter the six-digit code from your authenticator.');
  }
  if (pending.kind === 'selection' || (pending.kind === 'setup' && !pending.associated)) {
    throw new Error('Complete authenticator setup before verification.');
  }

  return new Promise((resolve, reject) => {
    const callbacks = authenticationCallbacks(
      pending.user,
      pending.version,
      resolve,
      reject,
    );
    if (pending.kind === 'setup') {
      pending.user.verifySoftwareToken(code, 'CHAMBER authenticator', callbacks);
    } else {
      pending.user.sendMFACode(code, callbacks, 'SOFTWARE_TOKEN_MFA');
    }
  });
};

// setDataProviderRef is kept for API compatibility (App.tsx calls it) but
// getPermissions now calls GET /me directly, so the reference is no longer used.
export const setDataProviderRef = (_dp: any) => {};

const cognitoProvider: AuthProvider = {
  login: async ({ username, password }) => {
    const details = new AuthenticationDetails({
      Username: username,
      Password: password,
    });
    const cognitoUser = new CognitoUser({ Username: username, Pool: userPool });

    clearNewPasswordChallenge();
    userPool.getCurrentUser()?.signOut?.();
    invalidateSession();
    const version = authenticationVersion;
    return new Promise((resolve, reject) => {
      cognitoUser.authenticateUser(
        details,
        authenticationCallbacks(cognitoUser, version, resolve, reject),
      );
    });
  },

  logout: () => {
    clearNewPasswordChallenge();
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) cognitoUser.signOut();
    invalidateSession();
    setUnprovisioned(false);
    // Remove only Cognito SDK keys — preserves 'consent' and other app state.
    // (signOut() already clears the tokens; this handles edge cases.)
    Object.keys(localStorage)
      .filter((k) => k.includes('CognitoIdentityServiceProvider'))
      .forEach((k) => localStorage.removeItem(k));
    return Promise.resolve();
  },

  checkAuth: () => {
    try {
      const exp = currentIdToken && jwtDecode<{exp?:number}>(currentIdToken).exp;
      if (typeof exp === 'number' && exp * 1000 > Date.now()) return Promise.resolve();
    } catch { /* malformed session */ }
    invalidateSession();
    return Promise.reject(new Error('Session expired'));
  },

  checkError: (error: any) => {
    // A response from an obsolete request must never log out the newer user.
    if (isSessionChangedError(error)) return Promise.resolve();
    // 401 = expired/invalid token → reject triggers react-admin logout + login redirect
    if (error?.status === 401) return Promise.reject(error);
    // 403 = permission denied (token valid) → resolve, but show a visible notification
    // (debounced so a page firing five 403s shows only one)
    if (error?.status === 403) {
      dispatch403(
        error?.authorizationSessionRevision,
        error?.authorizationSessionGeneration,
        error?.authorizationOperation,
      );
    }
    return Promise.resolve();
  },

  getPermissions: async ({ signal } = {}): Promise<Permissions> => {
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) return Promise.reject();

    const decoded = getDecodedToken(cognitoUser);
    if (!decoded) return EMPTY_PERMISSIONS;

    try {
      const data = await fetchMe(signal);

      // API returns { provisioned: false } when own record is absent
      if (data?.provisioned === false) {
        setUnprovisioned(true);
        return EMPTY_PERMISSIONS;
      }

      setUnprovisioned(false);
      return {
        assignments: data.assignments ?? EMPTY_PERMISSIONS.assignments,
        capabilities: data.capabilities ?? EMPTY_PERMISSIONS.capabilities,
      };
    } catch (err: any) {
      if (isUnprovisionedError(err)) {
        setUnprovisioned(true);
        return EMPTY_PERMISSIONS;
      }
      throw err;
    }
  },

  getIdentity: async () => {
    const decoded = getDecodedToken();
    if (!decoded) return { id: '', fullName: '' };
    return {
      id: decoded.sub,
      fullName: decoded.email || decoded.sub,
    };
  },
};

export default cognitoProvider;

if (typeof window !== 'undefined') {
  window.addEventListener('storage', event => {
    if (!event.key || event.key.startsWith(`CognitoIdentityServiceProvider.${ClientId}.`)) {
      clearNewPasswordChallenge();
      invalidateSession();
    }
  });
}
